import type { TFunction } from 'i18next';
import { formatLocalizedDate } from '@/shared/utils/date';
import type { SubscriptionDetail } from '@services/requests/housien-services/AgenciesService.subscription';
import { PlanIcon } from './plan-tab.icons';
import type { StripFact } from './SubscriptionStrip';

const UNCHANGEABLE = new Set(['cancelled', 'expired']);

/** What buildStripFacts needs to know beyond the subscription. */
export interface StripActions {
  onChangeCycle?: () => void;
  onChangeCard: () => void;
}

/**
 * Whether the subscription still accepts changes.
 *
 * @param subscription - The subscription.
 * @returns True when actions should be offered.
 */
export function isChangeable(subscription: SubscriptionDetail): boolean {
  return !UNCHANGEABLE.has(subscription.subscription_status);
}

/**
 * Resolves the billing cycle display label.
 */
function cycleLabel(
  frequency: string | null,
  t: TFunction,
): string {
  if (!frequency) return t('subscription.plan.strip.cycleUnknown');
  const isYearly = frequency.toLowerCase().includes('year');
  return t(isYearly ? 'subscription.plan.strip.cycleYearly' : 'subscription.plan.strip.cycleMonthly');
}

/**
 * Whether the cycle-switch action should be offered.
 */
function canSwitchToYearly(
  changeable: boolean,
  frequency: string | null,
  onChangeCycle: (() => void) | undefined,
): boolean {
  if (!changeable || !onChangeCycle) return false;
  return !(frequency ?? '').toLowerCase().includes('year');
}

/**
 * Builds the renewal fact.
 */
function renewalFact(renewal: string | null, trialing: boolean, t: TFunction): StripFact {
  // In the trial the date is the first charge, and the end of the trial (RN-17).
  const key = trialing ? 'firstCharge' : 'nextCharge';
  return {
    key: 'renewal',
    icon: PlanIcon.calendar,
    label: renewal
      ? t(`subscription.plan.strip.${key}`, { date: renewal })
      : t('subscription.plan.strip.nextChargeUnknown'),
  };
}

/**
 * Builds the card fact.
 */
function cardFact(
  card: SubscriptionDetail['payment_method'],
  changeable: boolean,
  onChangeCard: () => void,
  t: TFunction,
): StripFact {
  return {
    key: 'card',
    icon: PlanIcon.card,
    label: card
      ? t('subscription.plan.strip.cardEnding', { last4: card.last_four })
      : t('subscription.plan.strip.noCard'),
    actionLabel: changeable
      ? t(card ? 'subscription.plan.strip.changeCard' : 'subscription.plan.strip.addCard')
      : undefined,
    onAction: changeable ? onChangeCard : undefined,
  };
}

/**
 * Builds the three facts of the subscription strip.
 *
 * Absent facts are stated rather than blanked: "billing cycle unknown" is
 * true, an empty slot is not. An action whose destination cannot work — a
 * yearly switch with no yearly plan to switch to — is omitted rather than
 * rendered as a link that opens a dead end.
 *
 * @param subscription - The subscription.
 * @param actions - The handlers the inline links call.
 * @param t - Translation function.
 * @returns The facts, in display order.
 */
export function buildStripFacts(
  subscription: SubscriptionDetail,
  actions: StripActions,
  t: TFunction,
): StripFact[] {
  const changeable = isChangeable(subscription);
  const frequency = subscription.billing?.billing_frequency ?? null;
  const renewal = formatLocalizedDate(subscription.next_renewal_date);

  return [
    {
      key: 'cycle',
      icon: PlanIcon.cycle,
      label: cycleLabel(frequency, t),
      actionLabel: canSwitchToYearly(changeable, frequency, actions.onChangeCycle)
        ? t('subscription.plan.strip.switchToYearly') : undefined,
      onAction: actions.onChangeCycle,
    },
    renewalFact(renewal, subscription.subscription_status === 'trialing', t),
    cardFact(subscription.payment_method, changeable, actions.onChangeCard, t),
  ];
}
